import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';

import {
  AiScreeningResponseStatus,
  AiScreeningResult,
  AiScreeningsResponse,
} from '@core/ai-screening-types';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import { OfferStatusIndicatorComponent } from '@core/offers-ui';
import {
  BadgeComponent,
  ButtonComponent,
  CollapsibleCardComponent,
  DividerComponent,
  HeadingComponent,
  IconComponent,
  RtTooltip,
  TruncateComponent,
} from '@core/user-interface-legacy';
import { ScreeningOffer } from '@recruitee/ai-screening-types';
import { getScreeningSummaryData } from '@recruitee/ai-screening-util';

import { AiScreeningResultsListComponent } from '../ai-screening-results-list/ai-screening-results-list.component';
import { ScreeningResultIndicatorComponent } from '../screening-result-indicator/screening-result-indicator.component';

@Component({
  selector: 'rt-screening-result-card',
  templateUrl: './screening-result-card.component.html',
  styleUrl: './screening-result-card.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CollapsibleCardComponent,
    HeadingComponent,
    ScreeningResultIndicatorComponent,
    TranslatePipe,
    OfferStatusIndicatorComponent,
    DividerComponent,
    RtTooltip,
    TruncateComponent,
    AiScreeningResultsListComponent,
    BadgeComponent,
    ButtonComponent,
    IconComponent,
  ],
})
export class ScreeningResultCardComponent {
  private readonly i18n = inject(I18nService);

  public screening = input.required<AiScreeningsResponse | null>();
  public usageLimitReached = input.required<boolean>();
  public editable = input(false);
  public hasScreenActions = input(false);
  public offer = input.required<ScreeningOffer>();
  public initialCollapsed = input(true);
  public showOfferTitle = input(false);
  public previewMode = input(false);

  public edit = output<AiScreeningResult>();
  public screen = output<number>();
  public rescreen = output<number>();

  protected screeningSummaryData = computed(() => {
    const screening = this.screening();
    return screening ? getScreeningSummaryData(screening.results, screening.status) : null;
  });
  protected forceCollapsed = computed(() => {
    const screeningSummaryData = this.screeningSummaryData();
    return screeningSummaryData
      ? (<AiScreeningResponseStatus[]>['failed', 'pending', 'in_progress']).includes(
          screeningSummaryData.screeningStatus,
        )
      : true;
  });

  protected actionsDisabled = computed(() => this.usageLimitReached() || !this.editable());

  protected disabledTooltip = computed(() => {
    if (!this.editable())
      return this.i18n.translate('app.ai_candidate_screening.results.no_permission');
    if (this.usageLimitReached())
      return this.i18n.translate('app.ai_candidate_screening.results.usage_limit_reached_tooltip');
    return '';
  });

  protected descriptionText = computed(() => {
    const validXofY = this.screeningSummaryData()?.validXofY;
    const unknownCount = this.screeningSummaryData()?.unknownCount;
    const screeningSummaryData = this.screeningSummaryData();
    if (!screeningSummaryData) {
      return this.i18n.translate('app.ai_candidate_screening.results.not_screened_yet');
    }
    const screeningStatus = screeningSummaryData.screeningStatus;
    if (screeningStatus === 'failed')
      return this.i18n.translate('app.ai_candidate_screening.results.no_data');
    if (screeningStatus === 'pending' || screeningStatus === 'in_progress')
      return this.i18n.translate('app.ai_candidate_screening.results.calculating_score');
    if (this.screeningSummaryData()?.isAllValid) {
      return this.i18n.translate('app.ai_candidate_screening.results.all_criteria_met');
    }
    return this.i18n.translate(
      unknownCount === 0
        ? 'app.ai_candidate_screening.results.x_of_y_criteria_met'
        : 'app.ai_candidate_screening.results.x_of_y_criteria_met_z_unknown',
      { XofY: validXofY, unknownCount: unknownCount },
    );
  });
}
